import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ShieldCheck,
  ArrowRight,
  Mail,
  Lock,
  Languages,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react";
import { formatApiError } from "../lib/api";
import { resetCookieConsent } from "../components/CookieConsent";

export default function LoginPage() {
  const { login } = useApp();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) {
      setErr(t("login.bad_credentials"));
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const u = await login(email.trim(), password);
      toast.success(`${t("login.title")} · ${u.name}`);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e, t("login.bad_credentials")));
    } finally {
      setBusy(false);
    }
  };

  const toggleLang = () => {
    const next = i18n.language?.startsWith("en") ? "es" : "en";
    i18n.changeLanguage(next);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 lg:px-12 h-16 flex items-center justify-between border-b border-stone-100">
        <div className="flex items-center gap-3">
          <img
            src="/canales-logo-mark.svg"
            alt="Canales Furniture"
            className="h-11 w-auto"
            draggable="false"
          />
          <span className="font-display text-base hidden sm:inline text-stone-700">{t("app_name")}</span>
        </div>
        <button
          onClick={toggleLang}
          className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-stone-100"
          data-testid="lang-toggle-login"
        >
          <Languages size={14} /> {i18n.language?.startsWith("en") ? "ES" : "EN"}
        </button>
      </header>

      <div className="flex-1 grid lg:grid-cols-5">
        {/* Left visual panel (40%) */}
        <section className="hidden lg:flex hero-night text-white relative lg:col-span-2 items-center px-12">
          <div className="relative z-10 max-w-md">
            <img
              src="/canales-logo.svg"
              alt="Canales Furniture"
              className="h-24 w-auto -ml-1"
              draggable="false"
            />
            <div className="inline-flex items-center gap-2 mt-6 px-3 py-1 rounded-full bg-white/10 text-[10px] font-bold tracking-wider uppercase backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FACC15]" />
              {t("login.badge")}
            </div>
            <h1 className="font-display text-5xl xl:text-6xl mt-5 leading-[1.02]">
              {t("login.hero_title")}
              <br />
              <span className="bg-gradient-to-r from-[#FACC15] via-[#FBBF24] to-white bg-clip-text text-transparent">
                {t("login.hero_title_2")}
              </span>
            </h1>
            <p className="text-stone-300 mt-5 text-sm md:text-base">{t("login.hero_desc")}</p>

            <ul className="mt-8 space-y-3 text-sm text-stone-200">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#FACC15]/20 grid place-items-center mt-0.5">
                  <Sparkles size={11} className="text-[#FACC15]" />
                </span>
                {t("login.bullet_1")}
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#FACC15]/20 grid place-items-center mt-0.5">
                  <Sparkles size={11} className="text-[#FACC15]" />
                </span>
                {t("login.bullet_2")}
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#FACC15]/20 grid place-items-center mt-0.5">
                  <Sparkles size={11} className="text-[#FACC15]" />
                </span>
                {t("login.bullet_3")}
              </li>
            </ul>
          </div>
        </section>

        {/* Right form panel (60%) */}
        <section className="lg:col-span-3 flex items-center justify-center p-6 sm:p-12 bg-white">
          <Card className="w-full max-w-md border-stone-200 p-8 sm:p-10 shadow-none" data-testid="login-card">
            <div className="canales-overline">{t("login.subtitle")}</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">{t("login.title")}</h2>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="canales-overline">{t("common.email")}</label>
                <div className="relative mt-1.5">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11"
                    placeholder="admin@canales.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="canales-overline">{t("common.password")}</label>
                <div className="relative mt-1.5">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    data-testid="login-password-input"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-9 h-11"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    tabIndex={-1}
                    data-testid="toggle-password"
                  >
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">
                  {err}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full h-11 brand-button gap-2 mt-1"
                data-testid="login-submit-button"
              >
                {busy ? t("login.signing_in") : t("common.enter")}
                <ArrowRight size={16} />
              </Button>
            </form>

            <div className="mt-5 text-xs text-stone-500 text-center">
              {t("login.forgot")}
            </div>
          </Card>
        </section>
      </div>

      <footer className="border-t border-stone-100 py-4 px-6 text-center text-xs text-stone-400">
        © {t("brand")} · {t("app_name")} · v2.1
        <span className="mx-2">·</span>
        <Link to="/sms-optin" className="underline hover:text-stone-600" data-testid="footer-sms-optin">SMS Opt-In</Link>
        <span className="mx-1.5">·</span>
        <Link to="/sms-terms" className="underline hover:text-stone-600" data-testid="footer-sms-terms">SMS Terms</Link>
        <span className="mx-1.5">·</span>
        <Link to="/privacy" className="underline hover:text-stone-600" data-testid="footer-privacy">Privacy</Link>
        <span className="mx-1.5">·</span>
        <Link to="/terms" className="underline hover:text-stone-600" data-testid="footer-terms">Terms</Link>
        <span className="mx-1.5">·</span>
        <button
          type="button"
          onClick={resetCookieConsent}
          className="underline hover:text-stone-600"
          data-testid="footer-cookie-preferences"
        >
          {t("cookies.manage")}
        </button>
      </footer>
    </div>
  );
}
